// Admin Manual Payments Tab Component
import React, { useState, useEffect } from 'react';
import { GroupBox } from '../shared/GroupBox';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Check, X, Eye, Clock, IndianRupee, AlertTriangle, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAdmin, API_URL } from './AdminContext';

export function AdminManualPaymentsTab() {
  const { manualPayments, manualPaymentStats, fetchManualPayments } = useAdmin();
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [fullSubmission, setFullSubmission] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const viewSubmissionDetails = async (submission) => {
    setSelectedSubmission(submission);
    setLoadingDetails(true);
    setShowDetailsDialog(true);
    
    try {
      const response = await fetch(`${API_URL}/api/manual-payments/${submission.submission_id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFullSubmission(data);
      }
    } catch (error) {
      toast.error('Failed to load submission details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedSubmission) return;
    setProcessing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/manual-payments/${selectedSubmission.submission_id}/approve`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Payment approved! Subscription activated.');
        setShowDetailsDialog(false);
        fetchManualPayments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to approve payment');
      }
    } catch (error) {
      toast.error('Failed to approve payment');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedSubmission) return;
    setProcessing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/manual-payments/${selectedSubmission.submission_id}/reject?reason=${encodeURIComponent(rejectionReason)}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Payment rejected.');
        setShowRejectDialog(false);
        setShowDetailsDialog(false);
        setRejectionReason('');
        fetchManualPayments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reject payment');
      }
    } catch (error) {
      toast.error('Failed to reject payment');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <GroupBox title="Manual Payment Approvals" className="p-4">
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-amber-500" />
              <div>
                <p className="text-2xl font-bold">{manualPaymentStats?.pending || 0}</p>
                <p className="text-sm text-muted-foreground">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{manualPaymentStats?.approved || 0}</p>
                <p className="text-sm text-muted-foreground">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <XCircle className="w-8 h-8 text-red-500" />
              <div>
                <p className="text-2xl font-bold">{manualPaymentStats?.rejected || 0}</p>
                <p className="text-sm text-muted-foreground">Rejected</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-primary/10 border-primary/30">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <IndianRupee className="w-8 h-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">₹{(manualPaymentStats?.total_approved_amount || 0).toLocaleString()}</p>
                <p className="text-sm text-muted-foreground">Total Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Submissions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Pending Payment Approvals ({manualPayments?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {manualPayments?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No pending payment submissions
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3">Submitted</th>
                    <th className="text-left p-3">User</th>
                    <th className="text-left p-3">Plan</th>
                    <th className="text-left p-3">Cycle</th>
                    <th className="text-right p-3">Amount</th>
                    <th className="text-left p-3">Transaction Ref</th>
                    <th className="text-center p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {manualPayments?.map((submission) => (
                    <tr key={submission.submission_id} className="border-b hover:bg-muted/30">
                      <td className="p-3">
                        {format(new Date(submission.submitted_at), 'dd MMM yyyy HH:mm')}
                      </td>
                      <td className="p-3">
                        <div className="font-medium">{submission.user_name || submission.user_email}</div>
                        <div className="text-xs text-muted-foreground">{submission.user_email}</div>
                      </td>
                      <td className="p-3">
                        <Badge variant="outline">{submission.plan_name}</Badge>
                      </td>
                      <td className="p-3 capitalize">{submission.billing_cycle}</td>
                      <td className="p-3 text-right font-medium">₹{submission.amount?.toLocaleString()}</td>
                      <td className="p-3">
                        <span className="font-mono text-xs bg-muted px-2 py-1 rounded">
                          {submission.transaction_ref}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => viewSubmissionDetails(submission)}
                            data-testid={`view-submission-${submission.submission_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="default"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => {
                              setSelectedSubmission(submission);
                              handleApprove();
                            }}
                            data-testid={`approve-submission-${submission.submission_id}`}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              setSelectedSubmission(submission);
                              setShowRejectDialog(true);
                            }}
                            data-testid={`reject-submission-${submission.submission_id}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Payment Submission Details</DialogTitle>
            <DialogDescription>
              Review the payment screenshot and details before approving
            </DialogDescription>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : fullSubmission && (
            <div className="space-y-4">
              {/* User Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                <div>
                  <Label className="text-xs text-muted-foreground">User</Label>
                  <p className="font-medium">{fullSubmission.user_name || fullSubmission.user_email}</p>
                  <p className="text-sm text-muted-foreground">{fullSubmission.user_email}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Submitted At</Label>
                  <p className="font-medium">
                    {format(new Date(fullSubmission.submitted_at), 'dd MMM yyyy HH:mm:ss')}
                  </p>
                </div>
              </div>
              
              {/* Plan Info */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                <div>
                  <Label className="text-xs text-muted-foreground">Plan</Label>
                  <p className="font-medium">{fullSubmission.plan_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Billing Cycle</Label>
                  <p className="font-medium capitalize">{fullSubmission.billing_cycle}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Amount</Label>
                  <p className="font-medium text-lg text-primary">₹{fullSubmission.amount?.toLocaleString()}</p>
                </div>
              </div>
              
              {/* Transaction Ref */}
              <div className="p-4 bg-muted/50 rounded-lg">
                <Label className="text-xs text-muted-foreground">Transaction Reference / UTR</Label>
                <p className="font-mono text-lg">{fullSubmission.transaction_ref}</p>
              </div>
              
              {/* Screenshot */}
              <div className="p-4 bg-muted/50 rounded-lg">
                <Label className="text-xs text-muted-foreground mb-2 block">Payment Screenshot</Label>
                <ScrollArea className="h-[300px] rounded-lg border">
                  {fullSubmission.screenshot_data && (
                    <img
                      src={`data:${fullSubmission.screenshot_type || 'image/png'};base64,${fullSubmission.screenshot_data}`}
                      alt="Payment Screenshot"
                      className="w-full object-contain"
                    />
                  )}
                </ScrollArea>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
              Close
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowDetailsDialog(false);
                setShowRejectDialog(true);
              }}
            >
              <X className="w-4 h-4 mr-2" />
              Reject
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={handleApprove}
              disabled={processing}
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Approve & Activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Payment Submission</DialogTitle>
            <DialogDescription>
              Provide a reason for rejection. The user will be notified.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Rejection Reason</Label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g., Payment not found in bank records, Screenshot unclear, Amount mismatch..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={processing}
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <X className="w-4 h-4 mr-2" />
              )}
              Reject Submission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </GroupBox>
  );
}
